import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { DriverSection } from "../../redux/onBoardingSlice.types";
import { buildRejectionPayload } from "./buildRejectionPayload";
import { DOC_STATE } from "./constants";
import { RejectionPayload } from "./types";
import { useSectionStates } from "./useSectionStates";
import { useDocStates } from "./useDocStates";

export interface UseDriverVerificationPanelProps {
  sections: DriverSection[];
  onReject: (payload: RejectionPayload) => void;
}

/**
 * Encapsulates all state, side effects, and derived data for DriverVerificationPanel.
 *
 * Separating this from rendering means:
 * - The hook is testable with `renderHook` + a mock dispatch (no DOM/MUI needed).
 * - The component becomes a pure renderer whose JSX can be snapshot-tested in isolation.
 */
export const useDriverVerificationPanel = ({
  sections,
  onReject,
}: UseDriverVerificationPanelProps) => {
  const dispatch: any = useDispatch();

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const sortedSections = useMemo(
    () => [...sections].sort((a, b) => a.sequenceOrder - b.sequenceOrder),
    [sections]
  );

  const {
    sectionStates,
    sectionPickerRefs,
    init: initSections,
    markViewed,
    handleReject: handleSectionReject,
    handleToggleField: handleSectionToggleField,
    handleSelectAllFields: handleSectionSelectAllFields,
    handleRejectConfirm: handleSectionRejectConfirm,
    handleRejectCancel: handleSectionRejectCancel,
    handleSelectReason: handleSectionSelectReason,
    handleCommentChange: handleSectionCommentChange,
    handleClearReason: handleSectionClearReason,
    handleUnreject: handleSectionUnreject,
  } = useSectionStates(sortedSections);

  const { docStates, sasUrls, docLoadingMap, initDocStates, makeDocHandlers } =
    useDocStates(dispatch);

  // ── Init state + IntersectionObserver when sections load ──────────────

  useEffect(() => {
    if (!sections.length) return;

    initSections(sections);

    const imageFieldIds = sections
      .flatMap((s) => s.fields)
      .filter((f) => f.type === "image" && f.key && f.value)
      .map((f) => f.fieldId);
    initDocStates(imageFieldIds);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = (entry.target as HTMLElement).dataset.sectionId;
            if (sectionId) markViewed(sectionId);
          }
        });
      },
      { root: scrollAreaRef.current, threshold: 0.1 }
    );

    const timer = setTimeout(() => {
      sections.forEach((s) => {
        const el = sectionRefs.current[s.sectionId];
        if (el) observer.observe(el);
      });
    }, 50);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [sections]); // sections is the only stable trigger; inner fns are stable callbacks

  // ── Derived state ─────────────────────────────────────────────────────

  const allImageFields = useMemo(
    () =>
      sections.flatMap((s) =>
        s.fields.filter((f) => f.type === "image" && f.key && f.value)
      ),
    [sections]
  );

  const anySectionRejected = sortedSections.some(
    (s) => Object.keys(sectionStates[s.sectionId]?.rejectedFields ?? {}).length > 0
  );
  const anyDocRejected = allImageFields.some(
    (f) => docStates[f.fieldId]?.state === DOC_STATE.REJECTED
  );
  const allDocsViewed = allImageFields.every(
    (f) => docStates[f.fieldId]?.viewed === true
  );

  const canApprove = allDocsViewed && !anySectionRejected && !anyDocRejected;
  const canReject = anySectionRejected || anyDocRejected;

  const handleFooterReject = useCallback(() => {
    if (canReject)
      onReject(
        buildRejectionPayload(sortedSections, sectionStates, allImageFields, docStates)
      );
  }, [canReject, onReject, sortedSections, sectionStates, allImageFields, docStates]);

  return {
    scrollAreaRef,
    sectionRefs,
    sortedSections,
    sectionStates,
    sectionPickerRefs,
    docStates,
    sasUrls,
    docLoadingMap,
    makeDocHandlers,
    canApprove,
    canReject,
    handleFooterReject,
    handleSectionReject,
    handleSectionToggleField,
    handleSectionSelectAllFields,
    handleSectionRejectConfirm,
    handleSectionRejectCancel,
    handleSectionSelectReason,
    handleSectionCommentChange,
    handleSectionClearReason,
    handleSectionUnreject,
  };
};
